import React, { useState, useEffect, useContext } from 'react';
import { NavigationContext } from '../../context/NavigationContext';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import './Projects.css';

export type ProjectStatus = 'Offen' | 'Geschlossen' | 'Archiviert';

// Backend API uses OPEN, CLOSED, ARCHIVED
type ApiProjectStatus = 'OPEN' | 'CLOSED' | 'ARCHIVED';

export interface Project {
    id: number;
    name: string;
    description: string | null;
    status: ApiProjectStatus;
    rtdKontoId: number | null;
    rtdKonto?: RtdKonto;
}

interface RtdKonto {
    id: number;
    accountName: string;
    institute: string;
    iban: string;
    bic: string;
    glaeubigerId: string;
}

const ProjectsComponent: React.FC = () => {
    const navigationContext = React.useContext(NavigationContext);
    const authContext = useContext(AuthContext);
    
    if (!navigationContext) {
        throw new Error('ProjectsComponent must be used within a NavigationProvider');
    }
    if (!authContext) {
        throw new Error('ProjectsComponent must be used within an AuthProvider');
    }
    
    const { navigateTo } = navigationContext;
    const { token } = authContext;

    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>(['Offen', 'Geschlossen']);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectDescription, setNewProjectDescription] = useState('');
    const [rtdKontos, setRtdKontos] = useState<RtdKonto[]>([]);
    const [selectedRtdKontoId, setSelectedRtdKontoId] = useState<number | null>(null);
    const [loadingRtdKontos, setLoadingRtdKontos] = useState(true);
    
    // Load projects from backend
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await fetch('/api/projects', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der Projekte');
                }

                const data: Project[] = await response.json();
                setProjects(data);
            } catch (error) {
                console.error('Error fetching projects:', error);
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Projekte');
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [token]);

    // Load RTD bank accounts
    useEffect(() => {
        const fetchRtdKontos = async () => {
            try {
                const response = await fetch('/api/bankaccounts', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der RTD-Konten');
                }

                const data: RtdKonto[] = await response.json();
                setRtdKontos(data);
                if (data.length > 0) {
                    setSelectedRtdKontoId(data[0].id);
                }
            } catch (error) {
                console.error('Error fetching RTD accounts:', error);
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der RTD-Konten');
            } finally {
                setLoadingRtdKontos(false);
            }
        };

        fetchRtdKontos();
    }, [token]);

    const handleAddProject = () => {
        setShowAddModal(true);
        // Reset selectedRtdKontoId to default (first account) when opening modal
        if (rtdKontos.length > 0) {
            setSelectedRtdKontoId(rtdKontos[0].id);
        }
    };

    const handleSubmitNewProject = async () => {
        if (!newProjectName.trim()) {
            toast.error('Projektname ist erforderlich');
            return;
        }

        try {
            const response = await fetch('/api/projects', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: newProjectName,
                    description: newProjectDescription || null,
                    status: 'OPEN',
                    rtdKontoId: selectedRtdKontoId
                }),
            });

            if (!response.ok) {
                throw new Error('Fehler beim Erstellen des Projekts');
            }

            const newProject: Project = await response.json();
            setProjects([newProject, ...projects]);
            setShowAddModal(false);
            setNewProjectName('');
            setNewProjectDescription('');
            toast.success('Projekt erfolgreich erstellt');
        } catch (error) {
            console.error('Error creating project:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Erstellen des Projekts');
        }
    };

    const handleCancelAddProject = () => {
        setShowAddModal(false);
        setNewProjectName('');
        setNewProjectDescription('');
        setSelectedRtdKontoId(rtdKontos.length > 0 ? rtdKontos[0].id : null);
    };

    const handleViewDetails = (projectId: number) => {
        navigateTo(`/projectdetails/${projectId}`);
    };

    // Convert API status to display status
    const apiStatusToDisplay = (status: ApiProjectStatus): ProjectStatus => {
        switch (status) {
            case 'OPEN': return 'Offen';
            case 'CLOSED': return 'Geschlossen';
            case 'ARCHIVED': return 'Archiviert';
            default: return 'Offen';
        }
    };

    // Convert display status to API status
    const displayStatusToApi = (status: ProjectStatus): ApiProjectStatus => {
        switch (status) {
            case 'Offen': return 'OPEN';
            case 'Geschlossen': return 'CLOSED';
            case 'Archiviert': return 'ARCHIVED';
            default: return 'OPEN';
        }
    };

    const handleStatusToggle = (status: ProjectStatus) => {
        setSelectedStatuses(prev => 
            prev.includes(status) 
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const getStatusColor = (status: ProjectStatus): string => {
        switch (status) {
            case 'Offen':
                return '#28a745'; // Green
            case 'Geschlossen':
                return '#007bff'; // Blue
            case 'Archiviert':
                return '#6c757d'; // Gray
            default:
                return '#6c757d';
        }
    };

    // Filter projects based on search term and selected statuses
    const filteredProjects = projects.filter(project => {
        // Filter by search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = 
                project.name.toLowerCase().includes(searchLower) ||
                (project.description && project.description.toLowerCase().includes(searchLower));
            if (!matchesSearch) return false;
        }

        // Filter by selected statuses (convert API status to display status for comparison)
        const displayStatus = apiStatusToDisplay(project.status);
        if (!selectedStatuses.includes(displayStatus)) {
            return false;
        }

        return true;
    });

    if (loading) {
        return <div className="projects-container"><div className="projects-loading">Lade Projekte...</div></div>;
    }

    return (
        <div className="projects-container">
            <div className="projects-header">
                <h1>Abrechnungsprojekte</h1>
                <button className="add-project-button" onClick={handleAddProject} aria-label="Add new project">
                    +
                </button>
            </div>

            <div className="search-filter-section">
                <div className="search-bar">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Suche nach Projektname oder Beschreibung..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')}>
                            ✕
                        </button>
                    )}
                </div>

                <div className="status-filter">
                    <label className="filter-label">Status:</label>
                    <div className="status-checkboxes">
                        {(['Offen', 'Geschlossen', 'Archiviert'] as ProjectStatus[]).map(status => (
                            <label key={status} className="status-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={selectedStatuses.includes(status)}
                                    onChange={() => handleStatusToggle(status)}
                                />
                                <span className="status-checkbox-text">{status}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="projects-list">
                {filteredProjects.length === 0 ? (
                    <p className="no-data">
                        {projects.length === 0 
                            ? 'Keine Projekte vorhanden' 
                            : 'Keine Projekte entsprechen den Filterkriterien'}
                    </p>
                ) : (
                    filteredProjects.map(project => {
                        const displayStatus = apiStatusToDisplay(project.status);
                        return (
                            <div key={project.id} className="project-item">
                                <div className="project-info">
                                    <div className="project-name-row">
                                        <h3 className="project-name">{project.name}</h3>
                                        <span 
                                            className="project-status-label"
                                            style={{ backgroundColor: getStatusColor(displayStatus) }}
                                        >
                                            {displayStatus}
                                        </span>
                                    </div>
                                    <p className="project-description">{project.description || 'Keine Beschreibung'}</p>
                                </div>
                                <button 
                                    className="details-button" 
                                    onClick={() => handleViewDetails(project.id)}
                                    aria-label={`View details for ${project.name}`}
                                >
                                    Details
                                </button>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Add Project Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={handleCancelAddProject}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Neues Projekt erstellen</h2>
                        <div className="form-group">
                            <label htmlFor="project-name">Projektname *</label>
                            <input
                                id="project-name"
                                type="text"
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                placeholder="z.B. Winterfest 2024"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="project-description">Beschreibung</label>
                            <textarea
                                id="project-description"
                                value={newProjectDescription}
                                onChange={(e) => setNewProjectDescription(e.target.value)}
                                placeholder="Optionale Beschreibung des Projekts"
                                rows={4}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="rtd-konto">RTD Bankkonto *</label>
                            <select
                                id="rtd-konto"
                                value={selectedRtdKontoId || ''}
                                onChange={(e) => setSelectedRtdKontoId(Number(e.target.value))}
                                disabled={loadingRtdKontos}
                                required
                            >
                                {rtdKontos.length === 0 ? (
                                    <option value="">Keine Konten verfügbar</option>
                                ) : (
                                    rtdKontos.map(konto => (
                                        <option key={konto.id} value={konto.id}>
                                            {konto.accountName} ({konto.iban})
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-button" onClick={handleCancelAddProject}>
                                Abbrechen
                            </button>
                            <button className="submit-button" onClick={handleSubmitNewProject}>
                                Erstellen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsComponent;
